// Pure calculation logic ported from the original HTML calculator.
// All formulas preserved exactly.

export const LOGO_URL = "/finlightenment-logo.png";

export const RESET_CTA_URL =
  process.env.REACT_APP_RESET_CTA_URL ||
  "https://finlightenment.mykajabi.com/Financial-foundation-reset-priority-list";

export function currency(n) {
  if (!isFinite(n)) return "$0";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

export function percent(n, digits) {
  return Number(n).toFixed(digits === undefined ? 1 : digits) + "%";
}

export function clamp(n, min, max) {
  return Math.min(Math.max(n, min), max);
}

export function futureValueWithGrowingContributions(
  currentBalance, annualContribution, years, nominalReturn, incomeGrowth
) {
  let balance = currentBalance;
  let contribution = annualContribution;
  for (let i = 0; i < years; i++) {
    balance = balance * (1 + nominalReturn) + contribution;
    contribution = contribution * (1 + incomeGrowth);
  }
  return balance;
}

export function inflationAdjusted(value, inflation, years) {
  return value / Math.pow(1 + inflation, years);
}

export function recommendedRateForAge(age) {
  if (age < 30) return 10;
  if (age < 40) return 12;
  if (age < 50) return 15;
  if (age < 60) return 18;
  return 20;
}

export const DEFAULT_INPUTS = {
  age: 35,
  retirementAge: 67,
  grossIncome: 100000,
  takeHomePay: 72000,
  currentBalance: 50000,
  retirementSavingRate: 10,
  nominalReturn: 7,
  inflation: 2.5,
  incomeGrowth: 3,
  marginalTaxRate: 22,
  needsPct: 50,
  wantsPct: 30,
  futurePct: 20,
  futureIncludesRetirement: "yes",
  extraSavingsPercent: 1,
};

export function compute(inputs) {
  const age = Number(inputs.age) || 0;
  let retirementAge = Number(inputs.retirementAge) || 0;
  const grossIncome = Math.max(Number(inputs.grossIncome) || 0, 0);
  const takeHomePay = Math.max(Number(inputs.takeHomePay) || 0, 0);
  const currentBalance = Math.max(Number(inputs.currentBalance) || 0, 0);
  const retirementSavingRate = Number(inputs.retirementSavingRate) || 0;
  const nominalReturn = (Number(inputs.nominalReturn) || 0) / 100;
  const inflation = (Number(inputs.inflation) || 0) / 100;
  const incomeGrowth = (Number(inputs.incomeGrowth) || 0) / 100;
  const marginalTaxRate = (Number(inputs.marginalTaxRate) || 0) / 100;
  const needsPct = Math.max(Number(inputs.needsPct) || 0, 0);
  const wantsPct = Math.max(Number(inputs.wantsPct) || 0, 0);
  const futurePct = Math.max(Number(inputs.futurePct) || 0, 0);
  const futureIncludesRetirement = inputs.futureIncludesRetirement || "yes";
  const extraSavingsPercent = Number(inputs.extraSavingsPercent) || 1;

  if (retirementAge < age) retirementAge = age;
  const yearsToRetirement = Math.max(retirementAge - age, 0);

  const totalPct = needsPct + wantsPct + futurePct;
  const normalizedNeedsPct = totalPct > 0 ? (needsPct / totalPct) * 100 : 50;
  const normalizedWantsPct = totalPct > 0 ? (wantsPct / totalPct) * 100 : 30;
  const normalizedFuturePct = totalPct > 0 ? (futurePct / totalPct) * 100 : 20;

  const needsAnnual = takeHomePay * (normalizedNeedsPct / 100);
  const wantsAnnual = takeHomePay * (normalizedWantsPct / 100);
  const futureAnnual = takeHomePay * (normalizedFuturePct / 100);
  const needsMonthly = needsAnnual / 12;
  const wantsMonthly = wantsAnnual / 12;
  const futureMonthly = futureAnnual / 12;

  const currentAnnualContribution = grossIncome * (retirementSavingRate / 100);
  const increasedAnnualContribution =
    grossIncome * ((retirementSavingRate + extraSavingsPercent) / 100);
  const extraAnnualContribution =
    increasedAnnualContribution - currentAnnualContribution;

  const annualBudgetDrivenRetirementContribution = futureAnnual;
  let effectiveAnnualContribution;
  if (futureIncludesRetirement === "yes") {
    effectiveAnnualContribution = Math.max(
      currentAnnualContribution,
      annualBudgetDrivenRetirementContribution
    );
  } else {
    effectiveAnnualContribution =
      currentAnnualContribution + annualBudgetDrivenRetirementContribution;
  }
  const effectiveIncreasedContribution =
    effectiveAnnualContribution + extraAnnualContribution;

  const currentFutureValue = futureValueWithGrowingContributions(
    currentBalance,
    effectiveAnnualContribution,
    yearsToRetirement,
    nominalReturn,
    incomeGrowth
  );
  const increasedFutureValue = futureValueWithGrowingContributions(
    currentBalance,
    effectiveIncreasedContribution,
    yearsToRetirement,
    nominalReturn,
    incomeGrowth
  );

  const currentToday = inflationAdjusted(currentFutureValue, inflation, yearsToRetirement);
  const increasedToday = inflationAdjusted(increasedFutureValue, inflation, yearsToRetirement);
  const extraImpactToday = increasedToday - currentToday;

  const rothTakeHomeReductionAnnual = extraAnnualContribution;
  const pretaxTakeHomeReductionAnnual =
    extraAnnualContribution * (1 - marginalTaxRate);
  const rothTakeHomeReductionMonthly = rothTakeHomeReductionAnnual / 12;
  const pretaxTakeHomeReductionMonthly = pretaxTakeHomeReductionAnnual / 12;
  const rothTakeHomeReductionDaily = rothTakeHomeReductionMonthly / 30;
  const pretaxTakeHomeReductionDaily = pretaxTakeHomeReductionMonthly / 30;

  const estimatedNetRatio =
    grossIncome > 0 ? (takeHomePay / grossIncome) * 100 : 0;
  const recommendedRate = recommendedRateForAge(age);
  const progressToTarget =
    recommendedRate > 0
      ? clamp((retirementSavingRate / recommendedRate) * 100, 0, 100)
      : 0;

  return {
    age, retirementAge, grossIncome, takeHomePay, currentBalance,
    retirementSavingRate, marginalTaxRate, futureIncludesRetirement,
    extraSavingsPercent, yearsToRetirement,
    totalPct, normalizedNeedsPct, normalizedWantsPct, normalizedFuturePct,
    needsAnnual, wantsAnnual, futureAnnual,
    needsMonthly, wantsMonthly, futureMonthly,
    currentAnnualContribution, extraAnnualContribution,
    currentFutureValue, increasedFutureValue,
    currentToday, increasedToday, extraImpactToday,
    rothTakeHomeReductionMonthly, rothTakeHomeReductionAnnual, rothTakeHomeReductionDaily,
    pretaxTakeHomeReductionMonthly, pretaxTakeHomeReductionAnnual, pretaxTakeHomeReductionDaily,
    estimatedNetRatio, recommendedRate, progressToTarget,
  };
}
